import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { cldImage } from "@/lib/cldImage";

const HERO = cldImage("https://images.unsplash.com/photo-1571877227200-a0d98ea607e9", { w: 1400 });

/**
 * Forgot-password page — takes an email, calls the backend, and always shows
 * the same "check your inbox" confirmation (registered / unregistered /
 * throttled — the response body is identical for anti-enumeration).
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      // Even network errors show the generic success copy — never leak.
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="forgot-page" className="min-h-screen bg-espresso text-ivory">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 min-h-screen grid md:grid-cols-12 gap-10 items-center py-16">
        <div className="md:col-span-6">
          <Link to="/login" className="label-xs text-ivory/60 link-underline">← Back to sign in</Link>
          <div className="mt-8 label-xs text-gold/80">Recovery</div>
          <h1 className="mt-4 font-serif text-4xl md:text-5xl leading-[0.95] tracking-tight text-ivory font-normal">
            Forgot your<br/>
            <span className="italic text-ivory/60 font-light">password?</span>
          </h1>

          {sent ? (
            <div className="mt-10 max-w-md border border-gold/40 bg-ivory/5 px-6 py-6" data-testid="forgot-sent-msg">
              <p className="font-serif italic text-ivory">Check your inbox.</p>
              <p className="mt-2 text-sm text-ivory/70">
                If that email is registered, we've sent a link to reset your password. It expires in one hour.
              </p>
              <Link to="/login" className="mt-5 inline-block label-xs text-ivory/70 link-underline">Back to sign in →</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 max-w-md space-y-3" data-testid="forgot-form">
              <p className="text-ivory/70 text-sm">Enter your email and we'll send you a link to set a new password.</p>
              <input
                type="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="forgot-email"
                className="w-full bg-transparent border border-ivory/30 px-4 py-3 text-ivory placeholder:text-ivory/40 focus:outline-none focus:border-ivory rounded-sm"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="forgot-submit"
                className="w-full px-8 py-4 bg-gold text-espresso rounded-full text-xs uppercase tracking-[0.24em] hover:bg-gold-hover disabled:opacity-40"
              >
                {busy ? "…" : "Send reset link"}
              </button>
            </form>
          )}
        </div>
        <div className="md:col-span-6 hidden md:block">
          <div className="aspect-[4/5] max-h-[640px] overflow-hidden bg-espresso/80">
            <img src={HERO} alt="Tiramisu" className="w-full h-full object-cover opacity-90" />
          </div>
        </div>
      </div>
    </main>
  );
}
